import { createRouter, createWebHistory } from 'vue-router'
import { useUser } from './stores/user'

/** ================================================================================ **/

const rewardRoutes = [
  {
    path: '/reward/rewards',
    component: () => import('./views/reward/RewardList.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin'],
    }
  },
  {
    path: '/reward/rewards/new',
    component: () => import('./views/reward/RewardAdd.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      roles: ['admin'],
    }
  },
  {
    path: '/reward/rewards/:reward_id',
    component: () => import('./views/reward/RewardEdit.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      roles: ['admin'],
    }
  },
  {
    path: '/reward/reward-shops',
    component: () => import('./views/reward/RewardShopList.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin'],
    }
  },
  {
    path: '/reward/reward-shops/new',
    component: () => import('./views/reward/RewardShopAdd.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      roles: ['admin'],
    }
  },
  {
    path: '/reward/reward-shops/:shop_id',
    component: () => import('./views/reward/RewardShopEdit.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      roles: ['admin'],
    }
  },
]

/** ================================================================================ **/

const costRoutes = [
  {
    path: '/reward/contracts',
    component: () => import('./views/reward/ContractList.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin'],
    }
  },
  {
    path: '/reward/cost-setup',
    component: () => import('./views/reward/CostSetup.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin'],
    }
  },
]

/** ================================================================================ **/

const reportRoutes = [
  {
    path: '/reports/summary',
    component: () => import('./views/reports/ReportSummary.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin'],
    }
  },
  {
    path: '/reports/export-summary',
    component: () => import('./views/reports/ExportSummary.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin'],
    }
  },
  {
    path: '/reports/export-per-shop',
    component: () => import('./views/reports/ExportPerShop.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin'],
    }
  },
  {
    path: '/reports/redemptions',
    component: () => import('./views/reports/RedemptionList.vue'),
    meta: {
      requiresAuth: true,
      roles: ['admin'],
    }
  },
]

/** ================================================================================ **/

const userRoutes = [
  {
    path: '/users',
    component: () => import('./views/users/UserList.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      roles: ['admin'],
    }
  },
  {
    path: '/users/new',
    component: () => import('./views/users/UserAdd.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      roles: ['admin'],
    }
  },
  {
    path: '/users/:user_id',
    component: () => import('./views/users/UserEdit.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      roles: ['admin'],
    }
  },
]

/** ================================================================================ **/

const _router = createRouter({
  history: createWebHistory(),
  routes: [
    ...rewardRoutes,
    ...costRoutes,
    ...reportRoutes,
    ...userRoutes,
    {
      path: '/webapp-tester',
      component: () => import('./views/WebAppTester.vue'),
      meta: {
        requiresAuth: true,
        requiresWebAppTester: true,
      }
    },
    { path: '/', redirect: '/reward/rewards' },
    { path: '/login', component: () => import('./views/Login.vue') },
    { path: '/:pathMatch(.*)*', redirect: '/' }
  ],
})

/** ================================================================================ **/

_router.beforeEach(async (to) => {
  const userStore = useUser()

  if (to.matched.some(record => record.meta.requiresAuth)) {
    // TRY TO LOAD USER FROM TOKEN
    if (!userStore.isLoggedIn) {
      const token = localStorage.getItem('LOCA_PORTAL_ACCESS_TOKEN')
      if (token) {
        try {
          await userStore.setCurrentUserFromToken(token)
        } catch (error) {
          console.error('Cannot set user from remembered access token', error)
        }
      }
    }

    // RE-CHECK LOGIN STATUS
    if (!userStore.isLoggedIn) {
      return { path: '/login' }
    }

    // CHECK ROLES
    const routeRoles = to.meta !== undefined && to.meta.roles !== undefined ? to.meta.roles : []
    if (!userStore.hasRoles(routeRoles)) {
      return { path: '/forbidden' }
    }

    // CHECK ADMIN
    if (to.meta.requiresAdmin && !userStore.isAdmin) {
      return { path: '/forbidden' }
    }

    // CHECK WEBAPP TESTER
    if (to.meta.requiresWebAppTester && !userStore.canTestWebApp) {
      return { path: '/forbidden' }
    }
  }
})

/** ================================================================================ **/

export const router = _router
